import { forwardRef } from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "./cn";

/**
 * Botón — primitivo único del admin (design system §6.2).
 *
 * Antes de este componente, las clases de botón se copiaban a mano en
 * ~244 lugares (54 archivos), con drift garantizado: foco visible que se
 * perdía, hovers inconsistentes, variantes de color fuera de paleta.
 * Todo eso vive ahora acá, en un solo lugar.
 *
 * Variantes:
 * - `primary`      — CTA principal (bronce `brand-700` relleno).
 * - `secondary`    — acción secundaria (blanco + ring).
 * - `destructive`  — acción peligrosa relleno (rose).
 * - `ghost`        — sutil, hover suave (íconos, acciones de barra).
 * - `link`         — texto inline sin fondo (acciones de fila: "Editar").
 * - `linkDanger`   — texto inline peligroso ("Eliminar").
 *
 * El foco usa `focus-visible` (sólo teclado) con ring de la familia
 * correspondiente. `type` es `"button"` por defecto para no disparar
 * submits accidentales.
 */
export type ButtonVariant =
  | "primary"
  | "secondary"
  | "destructive"
  | "ghost"
  | "link"
  | "linkDanger";

export type ButtonSize = "sm" | "md";

const VARIANT_CLS: Record<ButtonVariant, string> = {
  primary:
    "rounded-md bg-brand-700 text-white shadow-sm hover:bg-brand-800 focus-visible:ring-brand-500",
  secondary:
    "rounded-md bg-white text-neutral-700 ring-1 ring-neutral-300 shadow-sm hover:bg-neutral-50 focus-visible:ring-brand-500",
  destructive:
    "rounded-md bg-rose-600 text-white shadow-sm hover:bg-rose-700 focus-visible:ring-rose-500",
  ghost:
    "rounded-md text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:ring-brand-500",
  // VIS-11: el link se reconoce como link (texto de acento brand-700, 5,3:1).
  link: "rounded text-brand-700 hover:text-brand-800 hover:underline focus-visible:ring-brand-500",
  linkDanger:
    "rounded text-rose-600 hover:text-rose-700 focus-visible:ring-rose-500 disabled:hover:text-rose-600",
};

/** Padding por tamaño. Las variantes `link*` no llevan padding de fondo. */
const SOLID_SIZE_CLS: Record<ButtonSize, string> = {
  sm: "px-2.5 py-1 text-xs",
  md: "px-3 py-1.5 text-sm",
};
const LINK_SIZE_CLS: Record<ButtonSize, string> = {
  sm: "text-xs",
  md: "text-sm",
};
/** En pantallas táctiles (`pointer: coarse`) todo botón ofrece al menos
 *  44 px de alto, y 44×44 si es solo-ícono (auditoría UX-03). El ícono y el
 *  texto no cambian; en escritorio con mouse las tablas quedan igual. */
const TACTIL_CLS = "[@media(pointer:coarse)]:min-h-11";
const TACTIL_ICONO_CLS = "[@media(pointer:coarse)]:min-w-11";

/** Padding cuadrado para botones solo-ícono (sin label). */
const ICON_ONLY_CLS: Record<ButtonSize, string> = {
  sm: "p-1",
  md: "p-1.5",
};

const ICON_SIZE_CLS: Record<ButtonSize, string> = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Ícono lucide a la izquierda del label. */
  icon?: LucideIcon | undefined;
  /** Ícono lucide a la derecha (ej. chevron). */
  iconRight?: LucideIcon | undefined;
  /** Botón solo-ícono (padding cuadrado). Exige `aria-label`. */
  iconOnly?: boolean | undefined;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    icon: Icon,
    iconRight: IconRight,
    iconOnly = false,
    type = "button",
    className,
    children,
    ...rest
  },
  ref,
): JSX.Element {
  const isLink = variant === "link" || variant === "linkDanger";
  const iconCls = ICON_SIZE_CLS[size];
  const paddingCls = isLink
    ? LINK_SIZE_CLS[size]
    : iconOnly
      ? ICON_ONLY_CLS[size]
      : SOLID_SIZE_CLS[size];

  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        "inline-flex items-center justify-center gap-2 font-medium transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        VARIANT_CLS[variant],
        paddingCls,
        TACTIL_CLS,
        iconOnly && TACTIL_ICONO_CLS,
        className,
      )}
      {...rest}
    >
      {Icon && <Icon className={iconCls} strokeWidth={1.5} aria-hidden="true" />}
      {children}
      {IconRight && (
        <IconRight className={iconCls} strokeWidth={1.5} aria-hidden="true" />
      )}
    </button>
  );
});
